import type { ChatSpace } from "@codvero/shared";
import { BookOpen, Briefcase, Building2, Camera, ChartColumn, Code, Coffee, Folder, Globe, GraduationCap, Heart, House, Megaphone, Music, Palette, Rocket, ShoppingBag, Sparkles, Star, Utensils, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn.ts";

export const SPACE_ICON_COMPONENTS: Record<string, LucideIcon> = {
  folder: Folder,
  briefcase: Briefcase,
  house: House,
  megaphone: Megaphone,
  camera: Camera,
  palette: Palette,
  code: Code,
  book: BookOpen,
  rocket: Rocket,
  heart: Heart,
  star: Star,
  "shopping-bag": ShoppingBag,
  utensils: Utensils,
  music: Music,
  "graduation-cap": GraduationCap,
  building: Building2,
  chart: ChartColumn,
  globe: Globe,
  sparkles: Sparkles,
  coffee: Coffee,
};
export const DEFAULT_SPACE_COLOR = "#10b981";

const SIZES = { xs: "size-5 rounded", sm: "size-6 rounded-md", md: "size-8 rounded-lg", lg: "size-10 rounded-xl" } as const;
const ICON = { xs: "size-3", sm: "size-3.5", md: "size-4", lg: "size-5" } as const;

/** A space's mark: its logo if it has one, otherwise its icon on a tint of its color. */
export function SpaceBadge({ space, size = "sm", className }: { space: Pick<ChatSpace, "color" | "icon" | "logo" | "name">; size?: keyof typeof SIZES; className?: string }) {
  const color = space.color ?? DEFAULT_SPACE_COLOR;
  if (space.logo) return <img src={space.logo} alt="" className={cn(SIZES[size], "shrink-0 object-cover", className)} />;
  const Icon = SPACE_ICON_COMPONENTS[space.icon ?? "folder"] ?? Folder;
  return (
    <span aria-hidden className={cn(SIZES[size], "grid shrink-0 place-items-center", className)} style={{ backgroundColor: `${color}26`, color }}>
      <Icon className={ICON[size]} />
    </span>
  );
}

/**
 * A picked picture made small enough to keep with the space: drawn into a square of `px` pixels (cropped to the
 * middle) and saved as WebP (PNG where WebP is not available), so a phone photo becomes a few kilobytes.
 */
export async function logoFromFile(file: File, px = 128): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((ok, bad) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = () => bad(new Error("not an image"));
      i.src = url;
    });
    const side = Math.min(img.naturalWidth, img.naturalHeight);
    const canvas = document.createElement("canvas");
    canvas.width = px;
    canvas.height = px;
    canvas.getContext("2d")!.drawImage(img, (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side, 0, 0, px, px);
    const webp = canvas.toDataURL("image/webp", 0.9);
    return webp.startsWith("data:image/webp") ? webp : canvas.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(url);
  }
}
